import { z } from 'zod'

import {
  EMISSION_OPENING_EVENT_TYPES,
  METHOD,
  OPENING_END_STATUS,
  RECEPTION_OPENING_EVENT_TYPES,
} from '../types/openingEvents.types.js'

// ─────────────────────────────────────────────────────────────────────────── //
//                         RECEPTION OPENING EVENTS                           //
//                              Kiosk → Galadrim                              //
// ─────────────────────────────────────────────────────────────────────────── //

// ─── method.selected ─────────────────────────────────────────────────────── //

const methodSelectedDataSchema = z.object({
  method: z.enum([METHOD.ACCOUNT, METHOD.CARD]),
  openingId: z.string(),
})

export type MethodSelectedEventData = z.infer<typeof methodSelectedDataSchema>

// ─── method.verified ─────────────────────────────────────────────────────── //

export const CARD_TYPES = ['cb', 'conecs'] as const
export type CardType = (typeof CARD_TYPES)[number]

const cardFieldsSchema = {
  cardNumber: z.string().nullish(),
  cardType: z.enum(CARD_TYPES).nullish(),
  cardBrand: z.string().nullish(),
}

const methodVerifiedDataSchema = z.discriminatedUnion('method', [
  z.object({
    method: z.literal(METHOD.ACCOUNT),
    accountId: z.number().nullish(),
    valid: z.boolean(),
    openingId: z.string(),
  }),
  z.object({
    method: z.literal(METHOD.CARD),
    valid: z.boolean(),
    openingId: z.string(),
    ...cardFieldsSchema,
  }),
  z.object({
    method: z.literal(METHOD.PREAUTHORIZATION),
    valid: z.boolean(),
    contactless: z.boolean().nullish(),
    preAuthorizationId: z.string().nullish(),
    preAuthorizationAmount: z.number().nullish(),
    preAuthorizationDuration: z.number().nullish(),
    openingId: z.string(),
    ...cardFieldsSchema,
  }),
])

export type MethodVerifiedEventData = z.infer<typeof methodVerifiedDataSchema>

// ─── opening.end ────────────────────────────────────────────────────────── //

const openingEndDataSchema = z.object({
  openingId: z.string(),
  status: z.enum([
    OPENING_END_STATUS.COMPLETED,
    OPENING_END_STATUS.CANCELLED,
    OPENING_END_STATUS.FAILED,
    OPENING_END_STATUS.TIMEOUT,
  ]),
  error: z.string().optional(),
})

export type OpeningEndEventData = z.infer<typeof openingEndDataSchema>

// ─── Reception event (discriminated union) ───────────────────────────────── //

export const receptionOpeningEventSchema = z.discriminatedUnion('type', [
  z.object({
    time: z.string(),
    type: z.literal(RECEPTION_OPENING_EVENT_TYPES.METHOD_SELECTED),
    data: methodSelectedDataSchema,
  }),
  z.object({
    time: z.string(),
    type: z.literal(RECEPTION_OPENING_EVENT_TYPES.METHOD_VERIFIED),
    data: methodVerifiedDataSchema,
  }),
  z.object({
    time: z.string(),
    type: z.literal(RECEPTION_OPENING_EVENT_TYPES.OPENING_END),
    data: openingEndDataSchema,
  }),
])

export type ReceptionOpeningEvent = z.infer<typeof receptionOpeningEventSchema>

// ─────────────────────────────────────────────────────────────────────────── //
//                          EMISSION OPENING EVENTS                           //
//                              Galadrim → Kiosk                              //
// ─────────────────────────────────────────────────────────────────────────── //

// ─── method.confirm or opening.confirm ───────────────────────────────────── //

export const confirmMethodSchema = z.object({
  granted: z.boolean(),
  method: z.enum([METHOD.ACCOUNT, METHOD.CARD, METHOD.PREAUTHORIZATION]),
})

export type ConfirmMethodData = z.infer<typeof confirmMethodSchema>

export const confirmMethodRequestSchema = confirmMethodSchema.extend({
  openingEventId: z.number().int().positive(),
})

export type ConfirmMethodRequestData = z.infer<typeof confirmMethodRequestSchema>

const methodConfirmDataSchema = confirmMethodSchema.extend({
  manual: z.boolean(),
  openingId: z.string(),
  preAuthorizationAmount: z.number().optional(),
  preAuthorizationDuration: z.number().optional(),
})

// ─── Emission event (discriminated union) ────────────────────────────────── //

export const emissionOpeningEventSchema = z.discriminatedUnion('type', [
  z.object({
    time: z.string(),
    type: z.enum([
      EMISSION_OPENING_EVENT_TYPES.METHOD_CONFIRM,
      EMISSION_OPENING_EVENT_TYPES.OPENING_CONFIRM,
    ]),
    data: methodConfirmDataSchema,
  }),
])

export type EmissionOpeningEvent = z.infer<typeof emissionOpeningEventSchema>
